import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { styles, getFallbackContainerStyle } from './equipment-image.styles'

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3']

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.trim().split(/\s+/)
  if (words.length === 1) return words[0][0].toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

const getColor = (name) => {
  if (!name) return COLORS[0]
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return COLORS[hash % COLORS.length]
}

const EquipmentImageFallback = ({ name, sx }) => {
  const initials = getInitials(name)
  const bgColor = getColor(name)
  const fontSize = typeof sx?.width === 'number' ? Math.max(8, Math.min(sx.width * 0.35, 48)) : 14
  // When height is 'auto', the fallback div collapses to text height unlike an img tag.
  // Use maxHeight or width as a square fallback to maintain visual weight.
  const resolvedHeight =
    sx?.height === 'auto' ? (sx?.maxHeight ?? sx?.width ?? 'auto') : (sx?.height ?? 'auto')

  return (
    <Box
      role="img"
      aria-label={name || 'Equipment image'}
      sx={[sx, styles.fallbackContainer, getFallbackContainerStyle(resolvedHeight, bgColor)]}
    >
      <Typography sx={[styles.initialsText, { fontSize }]}>{initials}</Typography>
    </Box>
  )
}

EquipmentImageFallback.propTypes = {
  name: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

export const EquipmentImage = ({ src, name, alt, sx, onError: onErrorProp, ...props }) => {
  const [hasError, setHasError] = useState(false)

  if (!src || hasError) {
    return <EquipmentImageFallback name={name} sx={sx} />
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt || name}
      sx={sx}
      {...props}
      onError={(e) => {
        setHasError(true)
        onErrorProp?.(e)
      }}
    />
  )
}

EquipmentImage.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  alt: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}
